// Reusable per-account ledger view: balance hero + transaction table.
// Used by the driver's own view AND the admin drill-in. Read-only by
// default; admin actions (reverse) only render when canManage is true.
//
// Layout: the CURRENT year is shown expanded (that's all a driver cares
// about — this year's deductions + additions). Prior years are collapsed
// into clickable rows at the bottom; click one to see that year's detail.
import { useState } from 'react';
import { formatMoney, formatMoneySigned } from '../utils/money';
import { formatDate as fmtDateCT } from '../utils/dateUtils';

const TYPE_LABELS = {
  deduction: 'Paycheck Deduction',
  nc_credit: 'No-Charge Credit',
  damage_charge: 'Damage Charge',
  cash_out: 'Cash Out',
  adjustment: 'Adjustment',
  opening_balance: 'Opening Balance',
  reversal: 'Reversal',
};
const TYPE_COLORS = {
  deduction: '#3b82f6',
  nc_credit: '#10b981',
  damage_charge: '#ef4444',
  cash_out: '#f59e0b',
  adjustment: '#8b5cf6',
  opening_balance: '#64748b',
  reversal: '#64748b',
};

function fmtDate(d) {
  if (!d) return '—';
  // occurred_on is a calendar date ("YYYY-MM-DD"), NOT a UTC timestamp.
  // Routing it through the UTC parser appends a 'Z' → "2025-01-01Z",
  // which Chrome rejects as Invalid Date. Parse the parts directly.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  if (m) {
    const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return fmtDateCT(d, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Year out of a "YYYY-MM-DD" occurred_on (falls back to created_at).
function txYear(t) {
  const s = String(t.occurred_on || t.created_at || '');
  const m = /^(\d{4})/.exec(s);
  return m ? m[1] : 'Undated';
}

export default function DamageFundLedger({ account, transactions, canManage = false, onReverse, onExport }) {
  const [openYears, setOpenYears] = useState(() => new Set());
  if (!account) return null;
  const bal = account.balance;
  const toggleYear = y => setOpenYears(prev => {
    const next = new Set(prev);
    next.has(y) ? next.delete(y) : next.add(y);
    return next;
  });
  const balColor = bal < 0 ? '#ef4444' : bal >= account.cap_threshold ? '#f59e0b' : '#10b981';
  const stateLine = account.at_cap
    ? `At cap — deductions paused (threshold ${formatMoney(account.cap_threshold)})`
    : account.underwater
      ? `Underwater — currently deducting ${formatMoney(account.biweekly_rate)} / pay period`
      : `Building — deducting ${formatMoney(account.biweekly_rate)} / pay period`;

  // Newest→oldest so the most recent activity is at the top, then bucket by year.
  const sorted = [...(transactions || [])].sort((a, b) =>
    String(b.occurred_on).localeCompare(String(a.occurred_on)) || (b.id - a.id)
  );
  const currentYear = String(new Date().getFullYear());
  const byYear = new Map();
  for (const t of sorted) {
    const y = txYear(t);
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y).push(t);
  }
  const currentTxns = byYear.get(currentYear) || [];
  const priorYears = [...byYear.keys()].filter(y => y !== currentYear).sort().reverse();

  return (
    <div>
      {/* Balance hero */}
      <div style={{
        background: '#0f172a', borderRadius: 16, padding: '28px 32px', color: '#fff',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16,
      }}>
        <div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8', fontWeight: 700 }}>
            {account.display_name}{account.region_group ? ` · ${account.region_group}` : ''}
          </div>
          <div style={{ fontSize: 44, fontWeight: 800, color: balColor, lineHeight: 1.1, marginTop: 6 }}>
            {formatMoneySigned(bal)}
          </div>
          <div style={{ fontSize: 13, color: '#cbd5e1', marginTop: 6 }}>{stateLine}</div>
        </div>
        <div style={{ display: 'flex', gap: 28, textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#10b981' }}>{formatMoney(account.nc_credit_total)}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>NC Credits</div>
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{account.tx_count}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Transactions</div>
          </div>
        </div>
      </div>

      {/* Transaction table — current year expanded, prior years collapsible */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 12px' }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
          {currentYear} Activity
        </h3>
        {onExport && (
          <button type="button" className="btn btn-gold btn-sm" onClick={onExport}>Export Excel</button>
        )}
      </div>

      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
          No transactions yet.
        </div>
      ) : (
        <>
          <TxnTable txns={currentTxns} emptyLabel={`No activity in ${currentYear} yet.`}
            canManage={canManage} onReverse={onReverse} />

          {priorYears.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b' }}>
                Previous Years
              </h4>
              {priorYears.map(y => {
                const yearTxns = byYear.get(y);
                const net = yearTxns.reduce((s, t) => s + (Number(t.amount) || 0), 0);
                const open = openYears.has(y);
                return (
                  <div key={y} style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8, overflow: 'hidden', background: '#fff' }}>
                    <button
                      type="button"
                      onClick={() => toggleYear(y)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ color: '#94a3b8', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
                        <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{y}</span>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{yearTxns.length} transaction{yearTxns.length === 1 ? '' : 's'}</span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Net</span>
                        <span style={{ fontWeight: 700, color: net < 0 ? '#ef4444' : '#10b981' }}>{formatMoneySigned(net)}</span>
                      </span>
                    </button>
                    {open && (
                      <div style={{ borderTop: '1px solid #f1f5f9' }}>
                        <TxnTable txns={yearTxns} canManage={canManage} onReverse={onReverse} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// One ledger table for a set of transactions (shared by current year +
// each expanded prior year).
function TxnTable({ txns, emptyLabel, canManage, onReverse }) {
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ whiteSpace: 'nowrap' }}>Date</th>
            <th>Type</th>
            <th>Description</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
            <th style={{ textAlign: 'right' }}>Balance</th>
            {canManage && <th></th>}
          </tr>
        </thead>
        <tbody>
          {txns.length === 0 && (
            <tr><td colSpan={canManage ? 6 : 5} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>{emptyLabel || 'No transactions.'}</td></tr>
          )}
          {txns.map(t => (
            <tr key={t.id} style={{ opacity: t.type === 'reversal' || t.reversed ? 0.6 : 1 }}>
              <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#64748b' }}>{fmtDate(t.occurred_on)}</td>
              <td>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                  background: `${TYPE_COLORS[t.type]}15`, color: TYPE_COLORS[t.type], textTransform: 'uppercase',
                }}>
                  {TYPE_LABELS[t.type] || t.type}
                </span>
              </td>
              <td style={{ fontSize: 13 }}>
                {t.description || '—'}
                {t.shop ? <span style={{ color: '#94a3b8' }}> · {t.shop}</span> : null}
                {t.reversed ? <span style={{ color: '#ef4444', fontWeight: 700, marginLeft: 6 }}>(reversed)</span> : null}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: t.amount < 0 ? '#ef4444' : '#10b981', whiteSpace: 'nowrap' }}>
                {formatMoneySigned(t.amount)}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                {formatMoneySigned(t.running_balance)}
              </td>
              {canManage && (
                <td style={{ textAlign: 'right' }}>
                  {t.type !== 'reversal' && !t.reversed && (
                    <button type="button" className="btn btn-muted btn-sm" onClick={() => onReverse(t)}>Reverse</button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
